import React, { useEffect, useState, useMemo } from 'react';
import Modal from './Modal';

interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  link: string;
  github?: string;
  category: string[];
  modalContent: {
    overview: string;
    tools: string[];
    liveLink?: string;
  };
}

const Projects: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);

  const projects: Project[] = useMemo(() => [
    {
      id: 'rivian',
      title: 'Rivian - Proudly Made in Georgia',
      description: 'As a Front-End Developer, I was responsible for building this site entirely within Squarespace, combining native builder tools with custom HTML, CSS, and JavaScript components. This project required creatively navigating Squarespace\'s limitations to achieve a polished, responsive design while ensuring custom elements worked seamlessly across the platform.',
      image: '/assets/png/rivian-website.png',
      technologies: ['HTML', 'CSS', 'JavaScript', 'Squarespace'],
      link: 'https://proudlymadeingeorgia.com',
      category: ['website'],
      modalContent: {
        overview: 'My role: Front End Developer. This project was built using Squarespace, with custom sections using HTML, CSS, and JAVASCRIPT. I was in charge of building the modules and sections on the front end and making sure that they are responsive from mobile to tablet to desktop.',
        tools: ['HTML', 'CSS', 'JavaScript', 'Squarespace'],
        liveLink: 'https://proudlymadeingeorgia.com'
      }
    },
    {
      id: 'spice-world',
      title: 'Spice World',
      description: 'For this WordPress project, I worked as a Front-End Developer using Elementor to construct and refine page layouts. Given the scale of the site, careful attention was required to ensure consistency and quality control across a large number of edits and newly developed sections.',
      image: '/assets/png/spice-world-website.png',
      technologies: ['WordPress', 'Elementor', 'Frontend Development'],
      link: 'https://spiceworldinc.com',
      category: ['website'],
      modalContent: {
        overview: 'For this WordPress project, I worked as a Front-End Developer using Elementor to construct and refine page layouts. Given the scale of the site, careful attention was required to ensure consistency and quality control across a large number of edits and newly developed sections.',
        tools: ['WordPress', 'Elementor', 'Frontend Development'],
        liveLink: 'https://spiceworldinc.com'
      }
    },
    {
      id: 'dovato',
      title: 'Dovato',
      description: 'In this role as a Front-End Developer, I built pages from the ground up using Adobe Experience Manager (AEM). I wrote all custom Sass and JavaScript to match precise design requirements, overcoming the limitations of AEM\'s minimalist framework to deliver fully tailored, client-approved pages.',
      image: '/assets/png/dovato-website.png',
      technologies: ['Sass', 'JavaScript', 'AEM', 'Frontend Development'],
      link: 'https://dovato.com',
      category: ['website'],
      modalContent: {
        overview: 'In this role as a Front-End Developer, I built pages from the ground up using Adobe Experience Manager (AEM). I wrote all custom Sass and JavaScript to match precise design requirements, overcoming the limitations of AEM\'s minimalist framework to deliver fully tailored, client-approved pages.',
        tools: ['Sass', 'JavaScript', 'AEM', 'Frontend Development'],
        liveLink: 'https://dovato.com'
      }
    },
    {
      id: 'pulse',
      title: 'Pulse AI - Industry Intelligence Platform',
      description: 'As a Full-Stack Developer and AI Engineer, I built Pulse AI from the ground up—a sophisticated intelligence platform that generates comprehensive industry reports using OpenAI\'s GPT models. The application features a multi-step input system, real-time AI analysis, and interactive dashboards with animated visualizations.',
      image: '/assets/png/pulse.png',
      technologies: ['React', 'TypeScript', 'OpenAI API', 'Framer Motion', 'Tailwind CSS', 'Vite'],
      link: 'https://industrypulse.netlify.app',
      category: ['web-app', 'ai'],
      modalContent: {
        overview: `As the sole developer on Pulse AI, I built a comprehensive industry intelligence platform using React, TypeScript, and OpenAI's GPT models. The application features a multi-step input system where users provide their industry, geography, company profile, and competitors, then receive AI-generated reports including executive summaries, trend analysis, competitor moves, and action plans. I implemented custom prompt engineering to ensure consistent, actionable insights, built interactive dashboards with Framer Motion animations, and created a profile system for saving different industry configurations. The platform demonstrates advanced React development with context-based state management, sophisticated AI integration, and responsive design that works across all devices.`,
        tools: ['React', 'TypeScript', 'OpenAI API', 'Framer Motion', 'Tailwind CSS', 'Vite', 'AI Prompt Engineering', 'Context API'],
        liveLink: 'https://industrypulse.netlify.app'
      }
    },
    {
      id: '4iq',
      title: '4iQ',
      description: 'As an AI Prompt Engineer and Full-Stack Developer, I handled both the front-end build,the back-end data connections, and the ai-generated summaries and content for this project. I developed the user-facing pages with HTML, Sass, and JavaScript, while also integrating SQL databases using Python. A key challenge was optimizing the apps\'s performance to handle high-traffic database calls efficiently, ensuring a smooth user experience.',
      image: '/assets/png/luckie-4is-app.png',
      technologies: ['HTML', 'Sass', 'JavaScript', 'Python', 'SQL'],
      link: 'https://4iq.luckie.com',
      category: ['web-app', 'datavis'],
      modalContent: {
        overview: 'As an AI Prompt Engineer and Full-Stack Developer, I handled both the front-end build, the back-end data connections, and the AI-generated summaries and content for this project. I developed the user-facing pages with HTML, Sass, and JavaScript, while also integrating SQL databases using Python. A key challenge was optimizing the app\'s performance to handle high-traffic database calls efficiently, ensuring a smooth user experience.',
        tools: ['HTML', 'Sass', 'JavaScript', 'Python', 'SQL', 'AI Prompting'],
        liveLink: 'https://4iq.luckie.com'
      }
    },
    {
      id: 'estimator',
      title: 'Service Selector',
      description: 'With my role as AI Prompt Engineer and Front End Developer, I initiated the development of the Service Selector by prompting an AI agent to generate the foundational structure and logic. From there, I took ownership of the codebase—refining, debugging, and extending the initial output to meet the desired functionality and user experience. This included finalizing layouts, optimizing performance, and ensuring the app was production-ready and deployed successfully.',
      image: '/assets/png/estimator.png',
      technologies: ['React', 'JavaScript', 'AI Prompting', 'Frontend Development'],
      link: 'https://devservselect.netlify.app',
      category: ['web-app'],
      modalContent: {
        overview: 'With my role as AI Prompt Engineer and Front End Developer, I initiated the development of the Service Selector by prompting an AI agent to generate the foundational structure and logic. From there, I took ownership of the codebase—refining, debugging, and extending the initial output to meet the desired functionality and user experience. This included finalizing layouts, optimizing performance, and ensuring the app was production-ready and deployed successfully.',
        tools: ['React', 'JavaScript', 'AI Prompting', 'Frontend Development'],
        liveLink: 'https://devservselect.netlify.app'
      }
    },
    {
      id: 'alabama-tourism',
      title: 'Alabama Tourism',
      description: 'As a Full-Stack Developer, I worked with Vue.js, Sanity CMS, and Tailwind CSS to build and update dynamic pages for Alabama Tourism. My focus was on maintaining high performance while expanding the site\'s structure through new and existing components.',
      image: '/assets/png/alabama-tourism-website.png',
      technologies: ['Vue.js', 'Sanity CMS', 'Tailwind CSS', 'Full-Stack Development'],
      link: 'https://tourism.alabama.gov',
      category: ['website'],
      modalContent: {
        overview: 'As a Full-Stack Developer, I worked with Vue.js, Sanity CMS, and Tailwind CSS to build and update dynamic pages for Alabama Tourism. My focus was on maintaining high performance while expanding the site\'s structure through new and existing components.',
        tools: ['Vue.js', 'Sanity CMS', 'Tailwind CSS', 'Full-Stack Development'],
        liveLink: 'https://tourism.alabama.gov'
      }
    },
    {
      id: 'uscrt',
      title: 'U.S. Civil Rights Trail',
      description: 'For this WordPress-powered project, I worked as a Front-End Developer to create reusable PHP components, develop new pages, and improve overall site performance. Working within a legacy codebase presented unique challenges, requiring careful navigation and modern enhancements without disrupting existing functionality.',
      image: '/assets/png/uscrt-website.png',
      technologies: ['WordPress', 'PHP', 'Frontend Development'],
      link: 'https://civilrightstrail.com',
      category: ['website'],
      modalContent: {
        overview: 'For this WordPress-powered project, I worked as a Front-End Developer to create reusable PHP components, develop new pages, and improve overall site performance. Working within a legacy codebase presented unique challenges, requiring careful navigation and modern enhancements without disrupting existing functionality.',
        tools: ['WordPress', 'PHP', 'Frontend Development'],
        liveLink: 'https://civilrightstrail.com'
      }
    },
    {
      id: 'money',
      title: 'Money Mindset',
      description: 'As a Front-End Developer, I built the Money Mindset Quiz —- an engaging personality quiz application that helps users discover their unique personality type through a 10-question assessment. The app features a modern design with gradient backgrounds, smooth animations, and five distinct personality types: The Strategist, The Optimist, The Pragmatist, The Enthusiast, and The Architect.',
      image: '/assets/png/money.png',
      technologies: ['React', 'JavaScript', 'Tailwind CSS', 'Local Storage'],
      link: 'https://money-mindset.netlify.app/',
      category: ['web-app'],
      modalContent: {
        overview: `As a Front-End Developer, I created the Money Mindset Quiz —- a modern personality quiz application built with React and Tailwind CSS. The app features a 10-question assessment that determines users' personality types through client-side calculations based on their responses. I implemented a clean, engaging user interface with gradient backgrounds and smooth transitions, built a local storage system to persist user responses, and created four distinct personality profiles with personalized insights and recommendations. The application includes a blog section for additional content and demonstrates responsive design principles with a mobile-first approach.`,
        tools: ['React', 'JavaScript', 'Tailwind CSS', 'Local Storage'],
        liveLink: 'https://money-mindset.netlify.app/'
      }
    }
  ], []);

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'web-app', name: 'Web Applications' },
    { id: 'website', name: 'Websites' },
    { id: 'ai', name: 'AI/ML Apps' },
    { id: 'datavis', name: 'Data Visualization' },
    // { id: 'games', name: 'Games' },
    // { id: 'saas', name: 'SaaS MVP' },
    // { id: 'analytics', name: 'Analytics Dashboards' },
  ];


 useEffect(() => {
    const element = document.getElementById('projects');
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before element is fully visible
      }
    );

    observer.observe(element);

    // Fallback for iOS: Check if element is already visible on mount
    const checkVisibility = () => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < windowHeight && rect.bottom > 0) {
        setIsVisible(true);
      }
    };

    checkVisibility();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(
        projects.filter(project => project.category.includes(activeFilter))
      );
    }
    // Reset showAllProjects when filter changes
    setShowAllProjects(false);
  }, [activeFilter, projects]);

  const openModal = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <section id="projects" className="section-padding">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <span className="text-white">Projects</span>
          </h2>
          <p className={`text-xl text-dark-300 max-w-3xl mx-auto transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Some of the work I'm most proud of.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveFilter(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeFilter === category.id
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-dark-800 text-dark-300 hover:bg-dark-700 border border-dark-600'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {(showAllProjects ? filteredProjects : filteredProjects.slice(0, 3)).map((project, index) => (
            <div
              key={project.id}
              className="card group overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Overlay Actions */}
                <div className="absolute inset-0 flex items-center justify-center space-x-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={() => openModal(project)}
                    className="bg-white text-dark-800 px-4 py-2 rounded-lg font-medium hover:bg-primary-600 hover:text-white transition-colors duration-300"
                  >
                    About this project
                  </button>
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-dark-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors duration-300"
                    >
                      GitHub
                    </a>
                  )}
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-dark-300 mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-primary-900 text-primary-200 text-xs font-medium rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

         {/* View More Button */}
        {filteredProjects.length > 3 && (
          <div className={`text-center mt-12 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <button
              onClick={() => setShowAllProjects(!showAllProjects)}
              className="group flex items-center gap-2 mx-auto px-6 py-3 bg-dark-800 text-white rounded-lg font-medium hover:bg-primary-600 transition-all duration-300 border border-dark-600 hover:border-primary-600"
            >
              <span>{showAllProjects ? 'Show Less' : 'View More Projects'}</span>
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${showAllProjects ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="text-lg text-dark-300 mb-6">
            Interested in working together? Feel free to reach out!
          </p>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
          >
            Contact
          </button>
        </div>
      </div>

      {/* Project Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedProject?.title || ''}
      >
        {selectedProject && (
          <div className="space-y-6">
            {/* Project Image */}
            <div className="relative">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full object-cover rounded-lg"
              />
            </div>

            {/* Project Overview */}
            <div>
              <h4 className="text-lg font-semibold text-dark-800 dark:text-white mb-3">
                Project Overview
              </h4>
              <p className="text-dark-600 dark:text-dark-300 leading-relaxed">
                {selectedProject.modalContent.overview}
              </p>
            </div>

            {/* Tools Used */}
            <div>
              <h4 className="text-lg font-semibold text-dark-800 dark:text-white mb-3">
                Tools Used
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedProject.modalContent.tools.map((tool) => (
                  <span
                    key={tool}
                    className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-sm font-medium rounded-full"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>

            {/* Live Link */}
            {selectedProject.modalContent.liveLink && (
              <div>
                <h4 className="text-lg font-semibold text-dark-800 dark:text-white mb-3">
                  See Live
                </h4>
                <a
                  href={selectedProject.modalContent.liveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block"
                >
                  Live Link
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

export default Projects;
